/**
 * Auth Context with Google Sign-In and Role Detection
 * Production-ready: Uses Cloud Functions for ID generation
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { auth, db, googleProvider, ADMIN_EMAIL } from '../config/firebase';
import {
  SALES_PARTNER_INVITES_COLLECTION,
  WEBSITE_OWNER_INVITES_COLLECTION,
  SALES_PARTNERS_COLLECTION,
  WEBSITE_OWNERS_COLLECTION,
  REFERRAL_CODES_COLLECTION,
  USERS_COLLECTION,
} from '../constants/adminInvites';

const AuthContext = createContext({
  user: null,
  userRole: null,
  userData: null,
  loading: true,
  isSalesPartner: false,
  isWebsiteOwner: false,
  signIn: async () => {},
  signOut: async () => {},
  refreshUserData: async () => {},
  refreshDashboardAccess: async () => {},
});

export const useAuth = () => useContext(AuthContext);

/** Use redirect flow on mobile; popup often blocked or broken */
function isMobileOrNarrow() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Debounce helper to prevent concurrent operations
const operationCache = new Map();
const DEBOUNCE_TIME = 1000; // 1 second

function debounceOperation(key, operation) {
  const now = Date.now();
  const cached = operationCache.get(key);
  
  if (cached && (now - cached.timestamp) < DEBOUNCE_TIME) {
    return cached.promise;
  }
  
  const promise = operation().catch(error => {
    // Remove from cache on error
    operationCache.delete(key);
    throw error;
  });
  
  operationCache.set(key, { promise, timestamp: now });
  
  // Clean up old cache entries
  setTimeout(() => {
    operationCache.delete(key);
  }, DEBOUNCE_TIME * 2);
  
  return promise;
}

// Retry helper for Firestore operations
async function retryFirestoreOperation(operation, maxRetries = 3, delay = 100) {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Suppress Firestore internal assertion errors
      if (error.message && error.message.includes('INTERNAL ASSERTION')) {
        console.warn(`Firestore internal error (attempt ${attempt + 1}/${maxRetries}), retrying...`);
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
          continue;
        }
      }
      
      // For other errors, only retry on network issues
      if (attempt < maxRetries - 1 && (
        error.code === 'unavailable' || 
        error.code === 'deadline-exceeded' ||
        error.message?.includes('network')
      )) {
        await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
        continue;
      }
      
      throw error;
    }
  }
  
  throw lastError;
}

/**
 * Claim a sales partner invite: create sales_partners/{uid}, referral_codes/{code}, delete invite.
 */
async function claimSalesPartnerInvite(uid, email, displayName, inviteDoc) {
  const data = inviteDoc.data();
  const code = (data.referralCode || '').trim().toUpperCase();
  if (!code) return;
  const salesPartnerRef = doc(db, SALES_PARTNERS_COLLECTION, uid);
  const codeRef = doc(db, REFERRAL_CODES_COLLECTION, code);
  await setDoc(salesPartnerRef, {
    email: email || '',
    name: displayName || data.name || '',
    referralCode: code,
    salesPartnerId: code,
    active: true,
    enabled: data.enabled !== false,
    status: 'approved',
    referralCodeActive: true,
    createdAt: serverTimestamp(),
  });
  await setDoc(codeRef, { salesPartnerId: uid, adminEnabled: data.enabled !== false });
  await deleteDoc(doc(db, SALES_PARTNER_INVITES_COLLECTION, inviteDoc.id));
}

/**
 * Claim a website owner invite: create/update users, website_owners/{uid}, referral_codes/{code}, delete invite.
 */
async function claimWebsiteOwnerInvite(uid, email, displayName, inviteDoc) {
  const data = inviteDoc.data();
  const code = (data.websiteOwnerCode || '').trim().toUpperCase();
  if (!code) return;
  const userRef = doc(db, USERS_COLLECTION, uid);
  const woRef = doc(db, WEBSITE_OWNERS_COLLECTION, uid);
  const codeRef = doc(db, REFERRAL_CODES_COLLECTION, code);
  const userData = {
    name: displayName || data.name || '',
    email: email || '',
    role: 'WEBSITE_OWNER',
    websiteOwnerCode: code,
    websiteOwnerId: code,
    profileCompleted: false,
    createdAt: serverTimestamp(),
  };
  await setDoc(userRef, userData);
  await setDoc(woRef, {
    email: email || '',
    name: displayName || data.name || '',
    websiteOwnerCode: code,
    enabled: data.enabled !== false,
    createdAt: serverTimestamp(),
  });
  await setDoc(codeRef, { ownerId: uid, isActive: data.enabled !== false });
  await deleteDoc(doc(db, WEBSITE_OWNER_INVITES_COLLECTION, inviteDoc.id));
}

/**
 * Determine user role based on email and Firestore data.
 * Claims both SP and WO invites when the same email has both, so both dashboard links can show.
 * Order: Admin -> claim any SP invite -> claim any WO invite -> existing SP -> existing WO -> default.
 */
async function determineUserRole(uid, email, displayName) {
  const cacheKey = `role-${uid}`;

  return debounceOperation(cacheKey, async () => {
    try {
      if (email === ADMIN_EMAIL) return 'ADMIN';

      const normalizedEmail = (email || '').trim().toLowerCase();
      if (!normalizedEmail) return 'WEBSITE_OWNER';

      // 1) Claim pending sales partner invite if any (so sales_partners/{uid} exists)
      const spInvitesSnap = await getDocs(
        query(
          collection(db, SALES_PARTNER_INVITES_COLLECTION),
          where('email', '==', normalizedEmail)
        )
      );
      if (!spInvitesSnap.empty) {
        const inviteDoc = spInvitesSnap.docs[0];
        const d = inviteDoc.data();
        if (d.enabled !== false) {
          await claimSalesPartnerInvite(uid, email, displayName, inviteDoc);
        }
      }

      // 2) Claim pending website owner invite if any (so website_owners/{uid} exists; same user can have both)
      const woInvitesSnap = await getDocs(
        query(
          collection(db, WEBSITE_OWNER_INVITES_COLLECTION),
          where('email', '==', normalizedEmail)
        )
      );
      if (!woInvitesSnap.empty) {
        const inviteDoc = woInvitesSnap.docs[0];
        const d = inviteDoc.data();
        if (d.enabled !== false) {
          await claimWebsiteOwnerInvite(uid, email, displayName, inviteDoc);
        }
      }

      // 3) Existing sales partner (by uid); respect enabled
      const salesPartnerDoc = await retryFirestoreOperation(() =>
        getDoc(doc(db, SALES_PARTNERS_COLLECTION, uid))
      );
      if (salesPartnerDoc.exists()) {
        const spData = salesPartnerDoc.data();
        if (spData.enabled === false) return 'DISABLED';
        return 'SALES_PARTNER';
      }

      // 4) Existing website owner; if disabled, no dashboard access
      const woDoc = await getDoc(doc(db, WEBSITE_OWNERS_COLLECTION, uid));
      if (woDoc.exists()) {
        if (woDoc.data().enabled === false) return 'DISABLED';
        return 'WEBSITE_OWNER';
      }

      return 'WEBSITE_OWNER';
    } catch (error) {
      if (error.message && error.message.includes('INTERNAL ASSERTION')) {
        console.warn('Firestore internal error in determineUserRole, defaulting to WEBSITE_OWNER');
        return 'WEBSITE_OWNER';
      }
      console.error('Error determining user role:', error);
      return 'WEBSITE_OWNER';
    }
  });
}

/**
 * Initialize user document in Firestore
 * Uses Cloud Functions for secure ID generation
 */
async function initializeUserDocument(user, role) {
  const cacheKey = `init-${user.uid}`;
  
  return debounceOperation(cacheKey, async () => {
    try {
      const userRef = doc(db, 'users', user.uid);
      
      // Retry logic for getDoc
      const userDoc = await retryFirestoreOperation(
        () => getDoc(userRef)
      );

      if (!userDoc.exists()) {
        const userData = {
          name: user.displayName || '',
          email: user.email || '',
          role: role,
          profileCompleted: false,
          websiteOwnerId: '',
          salesPartnerId: '',
          createdAt: serverTimestamp()
        };

        if (role === 'WEBSITE_OWNER') {
          const woDoc = await getDoc(doc(db, WEBSITE_OWNERS_COLLECTION, user.uid));
          if (woDoc.exists()) {
            const woData = woDoc.data();
            userData.websiteOwnerId = woData.websiteOwnerCode || userData.websiteOwnerId;
          } else {
            const timestamp = Date.now().toString(36).toUpperCase();
            const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
            userData.websiteOwnerId = `WO${timestamp}${randomStr}`;
          }
        } else if (role === 'SALES_PARTNER') {
          const salesPartnerRef = doc(db, SALES_PARTNERS_COLLECTION, user.uid);
          const salesPartnerDoc = await retryFirestoreOperation(() => getDoc(salesPartnerRef));
          if (salesPartnerDoc.exists()) {
            userData.salesPartnerId = salesPartnerDoc.data().salesPartnerId || salesPartnerDoc.data().referralCode || '';
          } else {
            const timestamp = Date.now().toString(36).toUpperCase();
            const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
            userData.salesPartnerId = `SP${timestamp}${randomStr}`;
            const hexChars = '0123456789ABCDEF';
            let referralCode = 'SP';
            for (let i = 0; i < 6; i++) {
              referralCode += hexChars.charAt(Math.floor(Math.random() * hexChars.length));
            }
            await retryFirestoreOperation(() =>
              setDoc(salesPartnerRef, {
                email: user.email || '',
                salesPartnerId: userData.salesPartnerId,
                referralCode,
                active: true,
                enabled: true,
                createdAt: serverTimestamp()
              })
            );
          }
        }

        await retryFirestoreOperation(() => setDoc(userRef, userData));
        return userData;
      }

      return userDoc.data();
    } catch (error) {
      // Suppress Firestore internal assertion errors
      if (error.message && error.message.includes('INTERNAL ASSERTION')) {
        console.warn('Firestore internal error in initializeUserDocument, returning basic data');
        return {
          name: user.displayName || '',
          email: user.email || '',
          role: role,
          profileCompleted: false
        };
      }
      console.error('Error initializing user document:', error);
      // Return basic user data on error to prevent app crash
      return {
        name: user.displayName || '',
        email: user.email || '',
        role: role,
        profileCompleted: false
      };
    }
  });
}

/** Check if user has Sales Partner and/or Website Owner dashboard access (for header submenu) */
async function getDashboardAccess(uid) {
  try {
    const [spSnap, woSnap] = await Promise.all([
      getDoc(doc(db, SALES_PARTNERS_COLLECTION, uid)),
      getDoc(doc(db, WEBSITE_OWNERS_COLLECTION, uid)),
    ]);
    const isSalesPartner = spSnap.exists() && spSnap.data().enabled !== false;
    const isWebsiteOwner = woSnap.exists() && woSnap.data().enabled !== false;
    return { isSalesPartner, isWebsiteOwner };
  } catch (e) {
    return { isSalesPartner: false, isWebsiteOwner: false };
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSalesPartner, setIsSalesPartner] = useState(false);
  const [isWebsiteOwner, setIsWebsiteOwner] = useState(false);

  // Persist auth so redirect flow restores session when user returns to app
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.warn('Auth persistence set failed:', err);
    });
  }, []);

  // Handle return from redirect sign-in (mobile) – must run on every load
  useEffect(() => {
    let isMounted = true;
    getRedirectResult(auth)
      .then((result) => {
        if (!isMounted) return;
        if (result?.user) {
          // User signed in via redirect; onAuthStateChanged will update state
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error('Redirect sign-in error:', error);
        setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let authStateChangeTimeout;
    let isInitialLoad = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clear any pending operations
      if (authStateChangeTimeout) {
        clearTimeout(authStateChangeTimeout);
      }
      
      // For initial load, set user immediately to prevent redirect loops
      // For subsequent changes, debounce to prevent race conditions
      const processAuthState = async () => {
        if (!isMounted) return;
        
        try {
          if (firebaseUser) {
            // Set user immediately to prevent redirect to login
            if (!isMounted) return;
            setUser(firebaseUser);
            
            // Set loading to false early so protected routes don't redirect
            if (isMounted) {
              setLoading(false);
            }
            
            // Add delay before Firestore operations to prevent race conditions
            await new Promise(resolve => setTimeout(resolve, 50));
            
            if (!isMounted) return;
            
            // Determine role with error handling (async, non-blocking)
            determineUserRole(firebaseUser.uid, firebaseUser.email, firebaseUser.displayName)
              .then(role => {
                if (!isMounted) return;
                setUserRole(role);
                getDashboardAccess(firebaseUser.uid).then(({ isSalesPartner: sp, isWebsiteOwner: wo }) => {
                  if (isMounted) {
                    setIsSalesPartner(!!sp);
                    setIsWebsiteOwner(!!wo);
                  }
                }).catch(() => {});
                // Initialize or get user document with error handling (async, non-blocking)
                initializeUserDocument(firebaseUser, role)
                  .then(data => {
                    if (!isMounted) return;
                    setUserData(data);
                  })
                  .catch(error => {
                    // Suppress Firestore internal assertion errors
                    if (error.message && error.message.includes('INTERNAL ASSERTION')) {
                      console.warn('Firestore internal error in initializeUserDocument (usually harmless)');
                    } else {
                      console.error('Error initializing user document:', error);
                    }
                    // Set fallback data
                    if (isMounted) {
                      setUserData({
                        name: firebaseUser.displayName || '',
                        email: firebaseUser.email || '',
                        role: role,
                        profileCompleted: false
                      });
                    }
                  });
              })
              .catch(error => {
                // Suppress Firestore internal assertion errors
                if (error.message && error.message.includes('INTERNAL ASSERTION')) {
                  console.warn('Firestore internal error in determineUserRole (usually harmless)');
                } else {
                  console.error('Error determining user role:', error);
                }
                // Set fallback role; still fetch dashboard access so both SP and WO links can show when user has both
                const fallbackRole = firebaseUser.email === ADMIN_EMAIL ? 'ADMIN' : 'WEBSITE_OWNER';
                if (isMounted) {
                  setUserRole(fallbackRole);
                  setUserData({
                    name: firebaseUser.displayName || '',
                    email: firebaseUser.email || '',
                    role: fallbackRole,
                    profileCompleted: false
                  });
                }
                getDashboardAccess(firebaseUser.uid).then(({ isSalesPartner: sp, isWebsiteOwner: wo }) => {
                  if (isMounted) {
                    setIsSalesPartner(!!sp);
                    setIsWebsiteOwner(!!wo);
                  }
                }).catch(() => {});
              });
          } else {
            if (!isMounted) return;
            setUser(null);
            setUserRole(null);
            setUserData(null);
            setIsSalesPartner(false);
            setIsWebsiteOwner(false);
            setLoading(false);
          }
        } catch (error) {
          // Suppress Firestore internal assertion errors
          if (error.message && error.message.includes('INTERNAL ASSERTION')) {
            console.warn('Firestore internal error in auth state change (usually harmless)');
            // Set fallback state
            if (isMounted && firebaseUser) {
              setUser(firebaseUser);
              setUserRole(firebaseUser.email === ADMIN_EMAIL ? 'ADMIN' : 'WEBSITE_OWNER');
              setIsSalesPartner(false);
              setIsWebsiteOwner(false);
              setUserData({
                name: firebaseUser.displayName || '',
                email: firebaseUser.email || '',
                role: firebaseUser.email === ADMIN_EMAIL ? 'ADMIN' : 'WEBSITE_OWNER',
                profileCompleted: false
              });
              setLoading(false);
            }
          } else {
            console.error('Auth state change error:', error);
            if (isMounted) {
              setLoading(false);
            }
          }
        }
      };
      
      // For initial load, process immediately
      // For subsequent changes, debounce slightly
      if (isInitialLoad) {
        isInitialLoad = false;
        processAuthState();
      } else {
        authStateChangeTimeout = setTimeout(processAuthState, 50); // Small debounce for subsequent changes
      }
    });

    return () => {
      isMounted = false;
      if (authStateChangeTimeout) {
        clearTimeout(authStateChangeTimeout);
      }
      // Add delay before unsubscribe to ensure proper cleanup
      setTimeout(() => {
        unsubscribe();
      }, 150);
    };
  }, []);

  const signIn = async () => {
    try {
      // Prefer popup everywhere; many mobile browsers support it and we avoid redirect getting stuck on firebaseapp.com
      const useRedirect = isMobileOrNarrow();
      if (useRedirect) {
        try {
          const result = await signInWithPopup(auth, googleProvider);
          return result.user;
        } catch (popupError) {
          const code = popupError?.code || '';
          const shouldTryRedirect = code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request';
          if (shouldTryRedirect) {
            await signInWithRedirect(auth, googleProvider);
            return null;
          }
          throw popupError;
        }
      }
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const refreshUserData = async () => {
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        setUserData(snap.data());
      }
      // Also refresh dashboard access so both Sales Partner and Website Owner links show when user has both
      await refreshDashboardAccess();
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const refreshDashboardAccess = async () => {
    if (!user?.uid) return;
    try {
      const { isSalesPartner: sp, isWebsiteOwner: wo } = await getDashboardAccess(user.uid);
      setIsSalesPartner(!!sp);
      setIsWebsiteOwner(!!wo);
    } catch (e) {
      // keep current flags on error
    }
  };

  const value = {
    user,
    userRole,
    userData,
    loading,
    isSalesPartner,
    isWebsiteOwner,
    signIn,
    signOut,
    refreshUserData,
    refreshDashboardAccess
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
